import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import type { FC } from 'react';
import ScheduleEntry from '../models/ScheduleEntry';
import { conflictManager } from '../services/ConflictManager';
import { getColorForCourse, brightenHexColor, mapTailwindBorderToColor } from '../utils/colorUtils';
import ResizeHandle from './ResizeHandle';
import { FaRegClock } from 'react-icons/fa';
import { IoMdMicrophone, IoMdBook } from "react-icons/io";
import { IoBookOutline } from "react-icons/io5";



// Wrapper to satisfy TS JSX typing with react-icons under current React/TS versions
const MeetingIcon: React.FC<{ className?: string }> = ({ className }) => {
    const Icon = FaRegClock as unknown as React.ComponentType<{ className?: string }>;
    return <Icon className={className} />;
};

// Wrapper for microphone icon to appease TS JSX typings
const EventIcon: React.FC<{ className?: string }> = ({ className }) => {
    const Icon = IoMdMicrophone as unknown as React.ComponentType<{ className?: string }>;
    return <Icon className={className} />;
};

// Wrapper for microphone icon to appease TS JSX typings
const BookIcon: React.FC<{ className?: string }> = ({ className }) => {
    const Icon = IoMdBook as unknown as React.ComponentType<{ className?: string }>;
    return <Icon className={className} />;
};

interface ConflictReason {
    reason: string;
    severity: string;
}

interface PeriodInfo {
    id: string | number;
    name: string;
    color: string;
}

interface ScheduleItemCardProps {
    entry: ScheduleEntry;
    isAdmin: boolean;
    /** When true with admin user: show admin visuals (conflicts) but disable drag, resize, and edit context menu */
    readOnly?: boolean;
    style: React.CSSProperties;
    formatGroupNames: (groups: any[]) => string;
    onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void;
    onPointerDown?: (e: React.PointerEvent<HTMLDivElement>) => void;
    nativeDraggable?: boolean;
    onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    onConflictIconClick: (e: React.MouseEvent, reasons: ConflictReason[]) => void;
    eventLetterSpacing?: string; // e.g., '0.3em'
    // Context menu handlers
    onEdit?: (entry: ScheduleEntry) => void;
    onCopy?: (entry: ScheduleEntry) => void;
    onDuplicate?: (entry: ScheduleEntry) => void;
    onDelete?: (entry: ScheduleEntry) => void;
    // Resize handlers
    onResizeStart?: (itemId: string, edge: 'top' | 'bottom', mouseY: number) => void;
    // Save animation
    isSaveAnimationActive?: boolean;
    // Highlight animation
    isHighlighted?: boolean;
    /** Selection ring when in copy/compare selection mode */
    isSelected?: boolean;
    /** Visual treatment for entries shown from overlapping periods other than the selected one */
    isSecondaryPeriod?: boolean;
    periodInfo?: PeriodInfo;
    // Overlap expansion (shared time/room slots with divided width)
    isOverlapping?: boolean;
    isOverlapExpanded?: boolean;
    onOverlapHoverStart?: () => void;
    onOverlapHoverEnd?: () => void;
}

const ScheduleItemCard: FC<ScheduleItemCardProps> = ({
    entry,
    isAdmin,
    readOnly = false,
    style,
    formatGroupNames,
    onDragStart,
    onDragEnd,
    onPointerDown,
    nativeDraggable = true,
    onClick,
    onConflictIconClick,
    eventLetterSpacing = '0.45em',
    onEdit,
    onCopy,
    onDuplicate,
    onDelete,
    onResizeStart,
    isSaveAnimationActive = false,
    isHighlighted = false,
    isSelected = false,
    isSecondaryPeriod = false,
    periodInfo,
    isOverlapping = false,
    isOverlapExpanded = false,
    onOverlapHoverStart,
    onOverlapHoverEnd,
}) => {
    const isEvent = entry.type === 'event';
    const isMeeting = entry.type === 'meeting';
    // Prefer subject-provided hex color when available; fallback to class-based palette
    const subjectHexColor = (entry as any)?.subjectObj?.color as string | undefined;
    const itemBgColor = subjectHexColor
        ? ''
        : getColorForCourse(entry.course || entry.type);

    const conflictIcon = isAdmin && !readOnly ? entry.getConflictIcon() : null;
    const canInteract = isAdmin && !readOnly;

    // Calculate card height from style to determine resize handle visibility
    const cardHeight = typeof style.height === 'string'
        ? parseFloat(style.height.replace(/[^\d.]/g, ''))
        : 0;

    // Show resize handles for admin users on cards taller than 30px
    // For cards less than 30px, only show bottom handle (requirement 1.5)
    const showTopHandle = Boolean(canInteract && onResizeStart && cardHeight >= 30);
    const showBottomHandle = Boolean(canInteract && onResizeStart);

    // Safari: grid-placed scroll children + backdrop / broad transitions /
    // handle opacity tweening composites badly WebKit-side.
    const resizeChromeSafe = Boolean(onResizeStart);
    const allowScaleHover = !resizeChromeSafe && !isOverlapping;
    const isExpandedOverlap = isOverlapping && isOverlapExpanded;
    const periodColor = periodInfo?.color || subjectHexColor || mapTailwindBorderToColor(itemBgColor);
    const periodName = periodInfo?.name;
    const brightenPeriodAccent = (color: string | undefined, amount: number) => {
        if (!color) return color;
        const normalized = color.startsWith('#') ? color : `#${color}`;
        if (/^#[0-9A-Fa-f]{6}$/.test(normalized)) {
            return brightenHexColor(normalized, amount);
        }
        return color;
    };
    const secondaryOutlineColor = brightenPeriodAccent(periodColor, 0.4);
    const secondaryBorderLeftColor = brightenPeriodAccent(periodColor, 0.55);
    const textClampClass = isExpandedOverlap ? 'whitespace-normal break-words' : 'truncate';
    const cardRef = useRef<HTMLDivElement>(null);
    const [expandedContentWidth, setExpandedContentWidth] = useState<number | null>(null);

    // Lock text layout to the final expanded width on hover so it doesn't reflow each animation frame.
    useLayoutEffect(() => {
        if (!isExpandedOverlap) {
            setExpandedContentWidth(null);
            return;
        }

        const card = cardRef.current;
        if (!card) return;

        const columnEl = card.offsetParent as HTMLElement | null;
        const columnWidth = columnEl?.clientWidth ?? 0;
        const widthStyle = style.width;
        if (columnWidth <= 0 || typeof widthStyle !== 'string') return;

        const percentMatch = widthStyle.match(/([\d.]+)%/);
        if (!percentMatch) return;

        let targetCardWidth = (columnWidth * parseFloat(percentMatch[1] ?? '0')) / 100;
        const fixedPxReductions = widthStyle.match(/-\s*(\d+(?:\.\d+)?)px/g);
        fixedPxReductions?.forEach((match) => {
            const px = parseFloat(match.replace(/[^\d.]/g, ''));
            if (!Number.isNaN(px)) targetCardWidth -= px;
        });

        // px-2 => 8px padding on each side
        setExpandedContentWidth(Math.max(0, targetCardWidth - 16));
    }, [isExpandedOverlap, style.width]);

    // Handle resize start events
    const handleResizeStart = (edge: 'top' | 'bottom') => (e: React.MouseEvent<HTMLDivElement>) => {
        if (!onResizeStart) return;
        e.preventDefault();
        e.stopPropagation();
        onResizeStart(entry.id, edge, e.clientY);
    };



    // Context menu state
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const contextMenuRef = useRef<HTMLDivElement>(null);

    // Handle right click to show context menu
    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Only show context menu for interactive admin users
        if (!canInteract) return;

        // Calculate position to avoid going off-screen
        const menuWidth = 120;
        const menuHeight = 120; // Approximate height for 3 items
        const x = e.clientX + menuWidth > window.innerWidth
            ? e.clientX - menuWidth
            : e.clientX;
        const y = e.clientY + menuHeight > window.innerHeight
            ? e.clientY - menuHeight
            : e.clientY;

        setContextMenuPosition({ x, y });
        setShowContextMenu(true);
    };

    // Handle clicks outside context menu to close it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
                setShowContextMenu(false);
            }
        };

        if (showContextMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('contextmenu', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('contextmenu', handleClickOutside);
        };
    }, [showContextMenu]);

    // Context menu handlers
    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowContextMenu(false);
        onEdit?.(entry);
    };

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowContextMenu(false);
        onCopy?.(entry);
    };

    const handleDuplicate = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowContextMenu(false);
        onDuplicate?.(entry);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowContextMenu(false);
        onDelete?.(entry);
    };

    const getAllConflictReasons = (): ConflictReason[] => {
        const allReasons: ConflictReason[] = [];
        entry.conflictsByWeek.forEach((conflictInfo, weekKey) => {
            if (conflictInfo.reasons && conflictInfo.reasons.length > 0) {
                const [week, year] = weekKey.split('-');
                conflictInfo.reasons.forEach((reason) => {
                    const text = `Week ${week}/${year}: ${reason.reason}`;
                    allReasons.push({
                        reason: text,
                        severity: reason.severity,
                    });
                });
            }
        });
        return allReasons;
    };

    const conflictReasons = getAllConflictReasons();

    const conflictReasonIds = conflictReasons.map((reason) =>
        conflictManager.makeConflictId(String(entry.id), reason.reason)
    );
    const allConflictsDismissed = conflictReasonIds.length > 0
        && conflictReasonIds.every(id => conflictManager.isConflictDismissed(id));

    // Use multiple teachers display if available, fallback to single teacher
    const teacherDisplay = (entry as any).teachersFormatted || entry.teacher || '';
    const periodTitle = periodName
        ? `\nPeriod: ${periodName}`
        : '';
    const baseTitle = `${entry.displayName}\nTeacher: ${teacherDisplay}\nGroups: ${entry.groups && entry.groups.length > 0 ? formatGroupNames(entry.groups) : 'All Groups'}\nTime: ${entry.formattedTime}\nRoom: ${entry.room}\nWeeks: ${entry.weekSeries || 'All Weeks'}${periodTitle}`;
    const finalTitle = isAdmin && entry.getConflictSummary()
        ? `${baseTitle}\nConflict: ${entry.getConflictSummary()}`
        : baseTitle;

    const cardStyle: React.CSSProperties = {
        ...style,
        ...(isExpandedOverlap
            ? {
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
            }
            : {}),
        transition: isOverlapping
            ? 'width 0.15s ease-out, left 0.15s ease-out, box-shadow 0.15s ease-out, height 0.1s ease-out, top 0.1s ease-out'
            : 'height 0.1s ease-out, top 0.1s ease-out',
    };

    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isOverlapping) {
            onOverlapHoverStart?.();
        }
        if (!allowScaleHover) return;
        if (!(e.target as Element).closest('[data-resize-handle]')) {
            (e.currentTarget as HTMLElement).style.transform = 'scale(1.02) translateY(-2px)';
        }
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isOverlapping) {
            onOverlapHoverEnd?.();
        }
        (e.currentTarget as HTMLElement).style.transform = '';
    };

    return (
        <>
            {/* CSS Animation Styles */}
            <style>
                {`
                    @keyframes saveShine {
                        0% {
                            transform: translateY(100%);
                            opacity: 0;
                        }
                        50% {
                            opacity: 0.8;
                        }
                        100% {
                            transform: translateY(-150%);
                            opacity: 0;
                        }
                    }
                    
                    @keyframes highlightPulse {
                        0%, 100% {
                            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
                        }
                        50% {
                            box-shadow: 0 0 20px 8px rgba(59, 130, 246, 0.6);
                        }
                    }
                    
                    .highlight-border {
                        animation: highlightPulse 2s ease-in-out;
                        border: 3px solid #3b82f6 !important;
                    }

                    .selection-border {
                        box-shadow: 0 0 0 3px #2563eb;
                        outline: 2px solid #ffffff;
                    }
                `}
            </style>
            <div
                ref={cardRef}
                data-schedule-entry-id={entry.id}
                className={`group absolute flex flex-col justify-start py-1 px-2 rounded-lg ${isEvent || isMeeting ? 'shadow-md' : ''
                    } border-l-4 text-[11px] leading-tight overflow-hidden ${isExpandedOverlap ? 'shadow-xl' : 'hover:shadow-xl'} ${isOverlapping ? '' : resizeChromeSafe ? 'transition-shadow duration-200' : 'transition-all duration-200'
                    } ${isSecondaryPeriod
                        ? 'bg-white text-gray-600 border-transparent'
                        : isEvent
                            ? 'bg-gradient-to-br from-[#A5B4FB] to-[#7C8CFB] text-white'
                            : isMeeting
                                ? 'bg-gradient-to-br from-green-400 via-green-400 to-green-500 text-white'
                                : itemBgColor
                    } ${canInteract ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${resizeChromeSafe ? '' : 'backdrop-blur-sm'} ${allowScaleHover ? 'transform' : ''} ${isHighlighted ? 'highlight-border' : ''} ${isSelected ? 'selection-border' : ''}`}
                style={{
                    ...cardStyle,
                    ...(subjectHexColor && !isEvent && !isMeeting && !isSecondaryPeriod ? { backgroundColor: brightenHexColor(subjectHexColor), borderLeftColor: subjectHexColor } : {}),
                    ...(isMeeting ? { borderLeftColor: '#2AB55C' } : {}),
                    ...(isSecondaryPeriod ? {
                        outline: `1px dashed ${secondaryOutlineColor}`,
                        outlineOffset: '-1px',
                        borderLeftColor: secondaryBorderLeftColor,
                    } : {}),
                    ...(canInteract ? { userSelect: 'none', WebkitUserSelect: 'none' } : {}),
                    ...(canInteract && !nativeDraggable ? { touchAction: 'none' } : {}),
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={(e) => {
                    const isOverResizeHandle = !!(e.target as Element).closest('[data-resize-handle]');
                    const el = e.currentTarget as HTMLElement;
                    if (allowScaleHover) {
                        if (isOverResizeHandle) {
                            el.style.transform = '';
                        } else {
                            el.style.transform = 'scale(1.02) translateY(-2px)';
                        }
                    }
                }}
                title={finalTitle}
                draggable={canInteract && nativeDraggable}
                onDragStart={canInteract && nativeDraggable ? onDragStart : undefined}
                onDragEnd={canInteract && nativeDraggable ? onDragEnd : undefined}
                onPointerDown={canInteract && !nativeDraggable ? onPointerDown : undefined}
                onClick={onClick}
                onContextMenu={handleContextMenu}
                data-type="scheduled_item"
            >
                {/* Resize Handles */}
                <ResizeHandle
                    position="top"
                    visible={showTopHandle}
                    reduceLayerMotion={resizeChromeSafe}
                    onMouseDown={handleResizeStart('top')}
                />
                <ResizeHandle
                    position="bottom"
                    visible={showBottomHandle}
                    reduceLayerMotion={resizeChromeSafe}
                    onMouseDown={handleResizeStart('bottom')}
                />
                {isAdmin && conflictIcon && (
                    <span
                        className={`absolute top-1 right-1 text-sm cursor-pointer conflict-icon-span hover:scale-110 transition-transform duration-150 z-10 bg-white/80 rounded-full w-5 h-5 flex items-center justify-center ${allConflictsDismissed ? 'opacity-30' : ''}`}
                        onClick={(e) => onConflictIconClick?.(e, conflictReasons)}
                        draggable="false"
                    >
                        {conflictIcon}
                    </span>
                )}

                <div className="w-full min-w-0 overflow-hidden">
                    {isEvent && (
                        <div
                            className="mb-0.5 -mx-2 -mt-1 px-1 font-normal uppercase text-center bg-blue-100/90 text-blue-500 flex items-center justify-center gap-1"
                            style={{ letterSpacing: eventLetterSpacing }}
                        >
                            <EventIcon className="text-green-700" aria-hidden="true" />
                            <span>EVENT</span>
                        </div>
                    )}
                    {isMeeting && (
                        <div
                            className="mb-0.5 -mx-2 -mt-1 px-1 font-normal uppercase text-center bg-green-100/90 text-green-700 flex items-center justify-center gap-1"
                            style={{ letterSpacing: eventLetterSpacing }}
                        >
                            <MeetingIcon className="text-green-700" aria-hidden="true" />
                            <span>MEETING</span>
                        </div>
                    )}
                    <div
                        className="overflow-hidden"
                        style={expandedContentWidth ? { width: expandedContentWidth } : undefined}
                    >
                        <p
                            className={`font-bold ${textClampClass} ${isEvent || isMeeting
                                ? 'text-white text-center uppercase'
                                : 'text-gray-900 flex items-center gap-1'
                                }`}
                        >
                            {isEvent || isMeeting ? (
                                entry.displayName
                            ) : (
                                <>
                                    <BookIcon className="text-gray-700 flex-shrink-0" aria-hidden="true" />
                                    <span className={textClampClass}>{entry.displayName}</span>
                                </>
                            )}
                        </p>
                        {teacherDisplay && (
                            <p className={`${textClampClass} text-xs mt-0 ${isEvent || isMeeting ? 'text-white text-center' : 'text-gray-700'}`}
                                title={teacherDisplay.length > 30 ? teacherDisplay : undefined}>{teacherDisplay}</p>
                        )}
                        {entry.groups && entry.groups.length > 0 && (
                            <p className={`${textClampClass} text-xs mt-0.5 ${isEvent || isMeeting ? 'text-white' : 'text-gray-600'}`}>
                                {formatGroupNames(entry.groups)}
                            </p>
                        )}
                        <p className={`${textClampClass} text-[10px] mt-1 font-medium ${isEvent || isMeeting ? 'text-white' : 'text-gray-500'}`}>{entry.room}</p>
                        <p className={`${textClampClass} text-[10px] font-semibold ${isEvent || isMeeting ? 'text-white' : 'text-gray-500'}`}>{entry.formattedTime}</p>
                    </div>
                </div>

                {periodName && (
                    <div
                        className={`mt-auto pt-1 flex items-center gap-1 min-w-0 text-[9px] font-semibold ${isEvent || isMeeting ? 'text-white' : 'text-gray-500'}`}
                        title={`Period: ${periodName}`}
                    >
                        <span
                            className="h-2 w-2 rounded-full flex-shrink-0 border border-white/80"
                            style={{ backgroundColor: periodColor }}
                            aria-hidden="true"
                        />
                        <span className="truncate">{periodName}</span>
                    </div>
                )}

                {/* Save Animation Overlay */}
                {isSaveAnimationActive && (
                    <div
                        className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg"
                        style={{
                            background: 'linear-gradient(0deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)',
                            animation: 'saveShine 1.2s ease-in-out',
                            zIndex: 10
                        }}
                    />
                )}
            </div>

            {/* Context Menu */}
            {showContextMenu && (
                <div
                    ref={contextMenuRef}
                    className="fixed bg-white border border-gray-200 rounded-md shadow-lg py-1 z-50 min-w-[120px]"
                    style={{
                        left: `${contextMenuPosition.x}px`,
                        top: `${contextMenuPosition.y}px`,
                    }}
                >
                    {onEdit && (
                        <button
                            onClick={handleEdit}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                        </button>
                    )}
                    {onCopy && (
                        <button
                            onClick={handleCopy}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy
                        </button>
                    )}
                    {onDuplicate && (
                        <button
                            onClick={handleDuplicate}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                            </svg>
                            Duplicate
                        </button>
                    )}
                    {onDelete && (
                        <button
                            onClick={handleDelete}
                            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                        </button>
                    )}
                </div>
            )}
        </>
    );
};

export default React.memo(ScheduleItemCard);


